import React, { useCallback, useMemo, useState } from "react";
import NotificationsContext from "./NotificationsContext";
import {
  useGetNotifications,
  useMarkNotificationAsRead,
} from "@/apis/app/notifications";
import { useQueryClient } from "@/contexts/HttpQueryContext";
import { useUser } from "@/contexts/UserContext";

export const NotificationsProvider = ({ children }) => {
  const { accessToken } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error, refetch } = useGetNotifications({
    page: currentPage,
    enabled: Boolean(accessToken),
    token: accessToken,
  });

  const { mutateAsync: mutateMarkAsRead } = useMarkNotificationAsRead({
    token: accessToken,
  });

  const result = data?.result ?? {};
  const notifications = result.notifications ?? [];
  const unreadCount = result.unread_count ?? 0;
  const pagination = result.pagination ?? {};
  const totalPages = pagination.last_page ?? 1;
  const totalCount = pagination.total ?? notifications.length;

  const setPage = useCallback((nextPage) => {
    setCurrentPage(Math.max(1, Number(nextPage) || 1));
  }, []);

  const markAsRead = useCallback(
    async (guid) => {
      if (!guid) return null;
      const response = await mutateMarkAsRead(guid);
      await queryClient.invalidateQueries({ queryKey: ["App::Notifications::List"] });
      return response?.result ?? null;
    },
    [mutateMarkAsRead, queryClient]
  );

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      totalCount,
      currentPage,
      totalPages,
      isLoading,
      isFetching,
      error,
      setPage,
      markAsRead,
      refetch,
    }),
    [
      notifications,
      unreadCount,
      totalCount,
      currentPage,
      totalPages,
      isLoading,
      isFetching,
      error,
      setPage,
      markAsRead,
      refetch,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
};
